import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileUpload } from "@/components/ui/file-upload";
import { useToast } from "@/hooks/use-toast";
import { projectApi } from "@/services/projectService";
import { Loader2 } from "lucide-react";

interface AttachmentFile extends File {
  id: string;
  preview?: string;
}

interface AddAdditionalDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  onDocumentAdded: () => void; // Callback to refresh the documents list
}

export const AddAdditionalDocumentModal = ({
  isOpen,
  onClose,
  projectId,
  onDocumentAdded,
}: AddAdditionalDocumentModalProps) => {
  const { toast } = useToast();
  const [tag, setTag] = useState("");
  const [files, setFiles] = useState<AttachmentFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const handleFilesChange = (newFiles: AttachmentFile[]) => {
    setFiles(newFiles);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!tag.trim()) {
      toast({
        title: "Tag is required",
        description: "Please enter a tag for the document",
        variant: "destructive",
      });
      return;
    }

    if (files.length === 0) {
      toast({
        title: "File is required",
        description: "Please select a file to upload",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);

    try {
      await projectApi.uploadAdditionalDocument(projectId, files[0], tag);

      toast({
        title: "Document uploaded successfully",
        description: `Document "${files[0].name}" has been uploaded with tag "${tag}"`,
      });

      // Reset form
      setTag("");
      setFiles([]);

      // Notify parent to refresh
      onDocumentAdded();

      // Close modal
      onClose();
    } catch (error: unknown) {
      console.error("Error uploading document:", error);
      const errorMessage =
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response &&
        error.response.data &&
        typeof error.response.data === "object" &&
        "error" in error.response.data
          ? String(error.response.data.error)
          : "Failed to upload document";

      toast({
        title: "Upload failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (isUploading) return; // Prevent closing while uploading
    setTag("");
    setFiles([]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Additional Document</DialogTitle>
          <DialogDescription>
            Upload an additional document with a descriptive tag.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Tag Input */}
            <div className="space-y-2">
              <Label htmlFor="tag">Document Tag</Label>
              <Input
                id="tag"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                placeholder="e.g., Site Photos, Additional Plans, etc."
                disabled={isUploading}
                required
              />
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label>Document File</Label>
              <FileUpload
                onFilesChange={handleFilesChange}
                maxFiles={1}
                maxSize={25 * 1024 * 1024} // 25MB
                acceptedFileTypes={{
                  "application/pdf": [".pdf"],
                  "application/msword": [".doc"],
                  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                    [".docx"],
                  "application/vnd.ms-excel": [".xls"],
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                    [".xlsx"],
                  "image/*": [".jpeg", ".jpg", ".png", ".gif", ".webp"],
                  "application/acad": [".dwg"],
                  "application/zip": [".zip"],
                  "text/plain": [".txt"],
                }}
                className="mt-2"
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isUploading || files.length === 0}>
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                "Upload Document"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddAdditionalDocumentModal;
